// frontend/src/pages/admin/DashboardPage.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { ConfirmDialog } from "../../components/ConfirmDialog"; // 🆕
import {
  Calendar,
  MapPin,
  Users,
  Edit,
  CheckCircle,
  QrCode,
  Plus,
  Download,
  Trash2,
  Palette,
  History,
  Settings,
  CreditCard,
} from "lucide-react";
import type { Event, EventStatus } from "../../types";
import QRCodeModal from "../../components/QRCodeModal";
import PlanBadge from "../../components/ui/PlanBadge";
import { useTranslation } from "react-i18next";
import dash from "./DashboardLook.module.css";

/**
 * 🆕 Composant de stats compactes pour le dashboard (V2)
 */
function EventStatsCompact({ eventId }: { eventId: string }) {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useQuery({
    queryKey: ["eventStatsDetailed", eventId],
    queryFn: () => api.getEventDetailedStats(eventId),
  });

  if (isLoading || !stats) return <div className="h-10 animate-pulse bg-gray-100 rounded-lg mt-4" />;

  return (
    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="bg-green-50 p-2 rounded-lg border border-green-100 text-center">
        <p className="text-[10px] text-green-600 font-bold uppercase tracking-wider">{t("admin.dashboard.revenue")}</p>
        <p className="text-sm font-black text-green-700">{stats.revenue.toFixed(2)} €</p>
      </div>
      <div className="bg-blue-50 p-2 rounded-lg border border-blue-100 text-center">
        <p className="text-[10px] text-blue-600 font-bold uppercase tracking-wider">{t("admin.dashboard.avgDuration")}</p>
        <p className="text-sm font-black text-blue-700">{stats.avgDuration} {t("common.minutes")}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // 🆕
  const { artist, logout } = useAuth();
  const queryClient = useQueryClient();

  // État pour la modale de notification
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  // 🆕 État pour la modale de confirmation (Oui/Non)
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: t("common.confirm"),
    danger: false,
  });

  const [qrModal, setQrModal] = useState({
    isOpen: false,
    event: null as any,
  });

  useEffect(() => {
    const checkOnboarding = async (): Promise<void> => {
      if (!artist?.id) return;
      try {
        const branding = await api.getBranding(artist.id);
        if (branding && branding.onboardingCompleted === false) {
          navigate("/admin/onboarding", { replace: true });
        }
      } catch {
        /* ignore */
      }
    };
    checkOnboarding();
  }, [artist?.id, navigate]);

  // Récupère les événements
  const { data: events, isLoading } = useQuery({
    queryKey: ["events", artist?.id],
    queryFn: () => api.getEventsByArtist(artist!.id),
    enabled: !!artist,
  });

  // Stats globales
  const { data: summary } = useQuery({
    queryKey: ["statsSummary"],
    queryFn: () => api.getStatsSummary(),
    enabled: !!artist,
  });

  // Mutation pour supprimer un événement
  const deleteMutation = useMutation({
    mutationFn: api.deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setModalState({
        isOpen: true,
        title: `✅ ${t("admin.dashboard.deleted")}`,
        message: t("admin.dashboard.deletedMessage"),
      });
    },
    onError: () => {
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: t("admin.dashboard.errorDeleting"),
      });
    },
  });

  // Mutation pour changer le statut
  const updateStatusMutation = useMutation({
    mutationFn: ({
      eventId,
      status,
    }: {
      eventId: string;
      status: EventStatus;
    }) => api.updateEvent(eventId, { status }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setModalState({
        isOpen: true,
        title: `✅ ${t("common.success")}`,
        message: t("admin.dashboard.statusChanged", { status: getStatusLabel(variables.status) }),
      });
    },
    onError: () => {
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: t("admin.dashboard.errorStatus"),
      });
    },
  });

  // 🆕 Fonction de suppression avec modale de confirmation
  const handleDelete = (eventId: string, eventName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t("common.delete"),
      message: `${t("common.confirmDelete") || "Voulez-vous vraiment supprimer"} "${eventName}" ?`,
      confirmText: t("common.delete"),
      danger: true,
      onConfirm: () => {
        deleteMutation.mutate(eventId);
      },
    });
  };

  const handleStatusChange = (eventId: string, newStatus: EventStatus) => {
    updateStatusMutation.mutate({ eventId, status: newStatus });
  };

  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "UPCOMING":
        return "bg-blue-100 text-blue-800";
      case "PAUSED":
        return "bg-yellow-100 text-yellow-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      case "COMPLETED":
        return "bg-slate-100 text-slate-800";
      case "CANCELLED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: EventStatus) => {
    const labels: Record<EventStatus, string> = {
      UPCOMING: t("status.upcoming") || "À venir",
      ACTIVE: t("status.active") || "Actif",
      PAUSED: t("status.paused") || "En pause",
      CLOSED: t("status.closed") || "Fermé",
      COMPLETED: t("status.completed") || "Terminé",
      CANCELLED: t("status.cancelled") || "Annulé",
    };
    return labels[status];
  };

  if (isLoading) {
    return (
      <div className={dash.page}>
        <p className={dash.hand}>{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div className={dash.page}>
      <div className="admin-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className={dash.hand}>{t("look.helloBiz")}</p>
              <h1 className={dash.title}>
                {t("brand.business")}
                {artist && <PlanBadge plan={(artist as any).plan ?? "FREE"} />}
              </h1>
              <p className={dash.sub}>{t("admin.dashboard.welcome")}</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigate("/admin/history")}
                className="admin-btn-outline flex-1 sm:flex-none"
              >
                <History className="w-4 h-4" />
                {t("admin.dashboard.history")}
              </button>
              <button
                onClick={() => navigate("/admin/integrations")}
                className="admin-btn-outline flex-1 sm:flex-none"
              >
                <Settings className="w-4 h-4" />
                {t("admin.dashboard.integrations")}
              </button>
              <button
                onClick={() => navigate("/admin/branding")}
                className="admin-btn-outline flex-1 sm:flex-none"
              >
                <Palette className="w-4 h-4" />
                {t("admin.dashboard.branding")}
              </button>
              <button
                onClick={() => navigate("/admin/billing")}
                className="admin-btn-outline flex-1 sm:flex-none"
              >
                <CreditCard className="w-4 h-4" />
                {t("admin.billing.title")}
              </button>
              <button
                onClick={() => navigate("/admin/events/create")}
                className="admin-btn-primary flex-1 sm:flex-none shadow-md"
              >
                <Plus className="w-4 h-4" />
                {t("admin.dashboard.createEvent")}
              </button>
              <button
                onClick={logout}
                className="flex-1 sm:flex-none px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                {t("admin.dashboard.logout")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cartes de statistiques globales */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <p className={dash.kicker}>{t("brand.insights")}</p>
        <div className={dash.kpis}>
          <div>
            <span>{t("admin.stats.clientsServed", "Clients servis")}</span>
            <b>{summary?.totalClientsServed ?? 0}</b>
          </div>
          <div>
            <span>{t("admin.stats.clientsWaiting", "En attente maintenant")}</span>
            <b>{summary?.clientsWaiting ?? 0}</b>
          </div>
          <div>
            <span>{t("admin.stats.activeEvents", "Événements actifs")}</span>
            <b>{summary?.activeEvents ?? 0}</b>
          </div>
          <div>
            <span>{t("admin.stats.totalEvents", "Événements au total")}</span>
            <b>{summary?.totalEvents ?? 0}</b>
          </div>
        </div>
      </div>

      {/* Liste des événements */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{t("admin.dashboard.myEvents")}</h2>

        {!events || events.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 mb-4">{t("admin.dashboard.noEvents")}</p>
            <button
              onClick={() => navigate("/admin/events/create")}
              className="px-6 py-3 admin-btn-primary"
            >
              {t("admin.dashboard.createFirst")}
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event: Event) => (
              <div
                key={event.id}
                className="admin-card"
              >
                {/* En-tête de la carte */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold text-gray-900">
                      {event.name}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                        event.status,
                      )}`}
                    >
                      {getStatusLabel(event.status)}
                    </span>
                  </div>

                  {event.description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {event.description}
                    </p>
                  )}

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(event.startDate).toLocaleDateString(i18n.language, {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>

                    {event.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {event._count?.clients || 0} {t("admin.dashboard.clientsRegistered")}
                    </div>
                  </div>

                  {/* 🆕 STATS DÉTAILLÉES (V2) */}
                  {event.status === "COMPLETED" && (
                     <EventStatsCompact eventId={event.id} />
                  )}


                  {/* Code et QR Code Ultra-Premium */}
                  <div className="mt-4 p-4 bg-slate-50 rounded-xl border-2 border-slate-200 group relative overflow-hidden">
                    <div className="flex items-center justify-between relative z-10">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                          {t("admin.dashboard.eventCode")}
                        </p>
                        <p className="text-3xl font-black text-slate-900 tracking-tighter">
                          {event.code}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setQrModal({ isOpen: true, event })}
                          className="relative w-12 h-12 bg-white p-1 rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer group/qr"
                          title={t("admin.dashboard.enlargeQr")}
                        >
                          <img
                            src={event.qrCodeUrl}
                            alt="QR Preview"
                            className="w-full h-full object-contain opacity-80 group-hover/qr:opacity-100"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover/qr:bg-slate-900/5 rounded-lg transition-colors">
                            <QrCode className="w-4 h-4 text-slate-700 opacity-0 group-hover/qr:opacity-100 transition-all" />
                          </div>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!event.qrCodeUrl) return;
                            const link = document.createElement("a");
                            link.href = event.qrCodeUrl;
                            link.download = `QR_${event.name}.png`;
                            link.click();
                          }}
                          className="p-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-900 hover:text-white transition-all shadow-sm cursor-pointer"
                          title={t("admin.dashboard.downloadQr")}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <QrCode className="absolute -right-2 -bottom-2 w-16 h-16 text-slate-200/40 rotate-12" />
                  </div>
                </div>

                {/* Changement rapide de statut */}
                <div className="px-6 py-3 bg-gray-50">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {t("common.status")} :
                  </label>
                  <select
                    value={event.status}
                    onChange={(e) =>
                      handleStatusChange(
                        event.id,
                        e.target.value as EventStatus,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-600"
                  >
                    <option value="UPCOMING">{t("status.upcoming")}</option>
                    <option value="ACTIVE">{t("status.active")}</option>
                    <option value="PAUSED">{t("status.paused")}</option>
                    <option value="CLOSED">{t("status.closed")}</option>
                    <option value="COMPLETED">{t("status.completed")}</option>
                    <option value="CANCELLED">{t("status.cancelled")}</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="p-4 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-2">
                  <button
                    onClick={() => navigate(`/admin/events/${event.id}`)}
                    className="flex-[2] min-w-[120px] px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t("admin.dashboard.viewQueue")}
                  </button>
                  <button
                    onClick={() => navigate(`/admin/events/${event.id}/edit`)}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    title={t("admin.dashboard.editEvent")}
                  >
                    <Edit className="w-4 h-4 mx-auto" />
                  </button>
                  <button
                    onClick={() => handleDelete(event.id, event.name)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    disabled={deleteMutation.isPending}
                    title={t("admin.dashboard.deleteEvent")}
                  >
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODALE DE NOTIFICATION (succès/erreur) */}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
      />

      {/* 🆕 MODALE DE CONFIRMATION (Oui/Non) */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        danger={confirmDialog.danger}
      />
      {/* Modal QR Code */}
      <QRCodeModal
        isOpen={qrModal.isOpen}
        onClose={() => setQrModal({ ...qrModal, isOpen: false })}
        qrCodeUrl={qrModal.event?.qrCodeUrl || ""}
        eventName={qrModal.event?.name || ""}
        eventCode={qrModal.event?.code || ""}
      />
    </div>
  );
}
